import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import {
  callEmbedAllowedOrigin,
  isCallEmbedMessage,
  requestCallEmbedSync,
} from "../lib/board/callEmbedBridge";

const SYNC_INTERVAL_MS = 2000;

export type CallEmbedRoomState = {
  embedInCall: boolean | null;
  callEnded: boolean;
  onIframeLoad: () => void;
  resetEmbedState: () => void;
  markCallEngaged: () => void;
};

/** Состояние звонка из iframe (postMessage + эвристики без SFU). */
export function useCallEmbedInRoom(
  enabled: boolean,
  iframeSrc: string,
  iframeRef: RefObject<HTMLIFrameElement | null>
): CallEmbedRoomState {
  const [embedInCall, setEmbedInCall] = useState<boolean | null>(null);
  const [callEnded, setCallEnded] = useState(false);
  const engagedRef = useRef(false);
  const iframeSrcRef = useRef(iframeSrc);
  iframeSrcRef.current = iframeSrc;

  const resetEmbedState = useCallback(() => {
    setEmbedInCall(null);
    setCallEnded(false);
    engagedRef.current = false;
  }, []);

  const markCallEngaged = useCallback(() => {
    engagedRef.current = true;
  }, []);

  const requestSync = useCallback(() => {
    requestCallEmbedSync(iframeRef.current);
  }, [iframeRef]);

  const onIframeLoad = useCallback(() => {
    if (engagedRef.current) {
      setEmbedInCall(false);
      setCallEnded(true);
      engagedRef.current = false;
    } else {
      setEmbedInCall(null);
      setCallEnded(false);
    }
    requestSync();
  }, [requestSync]);

  useEffect(() => {
    if (!enabled) {
      resetEmbedState();
      return;
    }

    const onMessage = (ev: MessageEvent) => {
      if (!isCallEmbedMessage(ev.data)) return;
      const fromOurIframe =
        iframeRef.current?.contentWindow != null &&
        ev.source === iframeRef.current.contentWindow;
      let originOk = callEmbedAllowedOrigin(ev.origin, iframeSrcRef.current);
      if (!originOk && !fromOurIframe) {
        try {
          originOk = new URL(ev.origin).hostname.endsWith("bezpart-server.ru");
        } catch {
          originOk = false;
        }
      }
      if (!fromOurIframe && !originOk) return;
      setEmbedInCall(ev.data.inCall);
      setCallEnded(!ev.data.inCall);
    };

    window.addEventListener("message", onMessage);
    requestSync();
    const syncTimer = window.setInterval(requestSync, SYNC_INTERVAL_MS);

    return () => {
      window.removeEventListener("message", onMessage);
      window.clearInterval(syncTimer);
      resetEmbedState();
    };
  }, [enabled, requestSync, iframeRef, resetEmbedState]);

  return {
    embedInCall,
    callEnded,
    onIframeLoad,
    resetEmbedState,
    markCallEngaged,
  };
}
